// src/storage/sprintStorage.ts — Local-First Persistence for Quest Sprint Engine (FR-QUEST-002)
// Tracks session results, streaks, and Quest Assessments SAS (Standardised Age Score, mean 200 aggregate).

export interface SprintAttempt {
  questionId: string;
  userAnswer: string;
  correct: boolean;
  secondsSpent: number;
}

export type QuestPaperDomain = 'paper1' | 'paper2_maths' | 'paper2_nvr';

export interface SprintSessionRecord {
  id: string;
  paper: QuestPaperDomain;
  paperTitle: string;
  totalQuestions: number;
  correctCount: number;
  accuracyPercent: number;
  estimatedSas: number;
  bestStreak: number;
  completed: boolean;
  durationSeconds: number;
  timestamp: number;
  attempts: SprintAttempt[];
}

export interface QuestionRegistryEntry {
  questionId: string;
  paper: QuestPaperDomain;
  timesSeen: number;
  timesCorrect: number;
  lastAttemptTimestamp: number;
  lastAttemptCorrect: boolean;
  consecutiveCorrect: number;
}

export interface SprintProgressData {
  sessions: SprintSessionRecord[];
  currentStreak: number;
  bestStreakOverall: number;
  totalSprintsCompleted: number;
  totalQuestionsAnswered: number;
  totalCorrect: number;
  lastSessionDate: string;
  questionRegistry: Record<string, QuestionRegistryEntry>;
  completedSetCounts: Record<QuestPaperDomain, number>;
}

const STORAGE_KEY = 'clawey_sprint_progress_v1';

function getDefaultProgress(): SprintProgressData {
  return {
    sessions: [],
    currentStreak: 0,
    bestStreakOverall: 0,
    totalSprintsCompleted: 0,
    totalQuestionsAnswered: 0,
    totalCorrect: 0,
    lastSessionDate: new Date().toISOString().split('T')[0] ?? '',
    questionRegistry: {},
    completedSetCounts: {
      paper1: 0,
      paper2_maths: 0,
      paper2_nvr: 0,
    },
  };
}

export class SprintStorage {
  /** Load all sprint session data from localStorage */
  static load(): SprintProgressData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return getDefaultProgress();
      const parsed = JSON.parse(raw) as Partial<SprintProgressData>;
      const defaults = getDefaultProgress();
      return {
        ...defaults,
        ...parsed,
        sessions: Array.isArray(parsed.sessions) ? [...parsed.sessions] : [],
        questionRegistry: parsed.questionRegistry && typeof parsed.questionRegistry === 'object'
          ? { ...parsed.questionRegistry }
          : {},
        completedSetCounts: {
          ...defaults.completedSetCounts,
          ...(parsed.completedSetCounts || {}),
        },
      };
    } catch (err) {
      console.warn('Failed to load sprint progress:', err);
      return getDefaultProgress();
    }
  }

  /** Persist sprint progress */
  static save(data: SprintProgressData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.warn('Failed to save sprint progress:', err);
    }
  }

  /**
   * Calculate simulated Quest Standardised Age Score (SAS)
   * Bexley Selection Test (Quest Assessments): Total SAS mean is 200 (aggregate of Paper 1 and 2).
   * Sub-scores are scaled so a baseline 50% maps to ~200 SAS, 75% maps to ~216 SAS, 100% maps to ~238 SAS.
   */
  static calculateQuestSas(correct: number, total: number): number {
    if (total <= 0) return 200;
    const ratio = Math.max(0, Math.min(1, correct / total));
    // Standard normal distribution approximation centered at 200, std dev 24
    // (ratio - 0.5) * 60 + 200, bounded between 140 and 245
    const rawSas = Math.round(200 + (ratio - 0.5) * 55);
    return Math.max(140, Math.min(245, rawSas));
  }

  /** Record a completed or abandoned sprint session */
  static recordSession(session: SprintSessionRecord): SprintProgressData {
    const data = this.load();
    data.sessions.push(session);

    if (session.completed) {
      data.totalSprintsCompleted += 1;
      data.completedSetCounts[session.paper] = (data.completedSetCounts[session.paper] || 0) + 1;
    }
    data.totalQuestionsAnswered += session.attempts.length;
    data.totalCorrect += session.correctCount;

    if (session.bestStreak > data.bestStreakOverall) {
      data.bestStreakOverall = session.bestStreak;
    }

    // Update per-question history in registry
    for (const attempt of session.attempts) {
      const existing = data.questionRegistry[attempt.questionId];
      if (existing) {
        existing.timesSeen += 1;
        if (attempt.correct) {
          existing.timesCorrect += 1;
          existing.consecutiveCorrect += 1;
        } else {
          existing.consecutiveCorrect = 0;
        }
        existing.lastAttemptCorrect = attempt.correct;
        existing.lastAttemptTimestamp = session.timestamp;
      } else {
        data.questionRegistry[attempt.questionId] = {
          questionId: attempt.questionId,
          paper: session.paper,
          timesSeen: 1,
          timesCorrect: attempt.correct ? 1 : 0,
          lastAttemptTimestamp: session.timestamp,
          lastAttemptCorrect: attempt.correct,
          consecutiveCorrect: attempt.correct ? 1 : 0,
        };
      }
    }

    data.lastSessionDate = new Date().toISOString().split('T')[0] ?? '';
    this.save(data);
    return data;
  }

  /** Retrieve registry stats for a specific question */
  static getQuestionStats(questionId: string): QuestionRegistryEntry | undefined {
    const data = this.load();
    return data.questionRegistry[questionId];
  }

  /** Retrieve IDs of questions that have been attempted at least once */
  static getSeenQuestionIds(paper?: QuestPaperDomain): string[] {
    const data = this.load();
    return Object.values(data.questionRegistry)
      .filter((entry) => (!paper || entry.paper === paper) && entry.timesSeen > 0)
      .map((entry) => entry.questionId);
  }

  /**
   * Retrieve IDs of questions that were missed in their most recent attempt
   * or have accuracy < 100%.
   */
  static getIncorrectQuestionIds(paper?: QuestPaperDomain): string[] {
    const data = this.load();
    return Object.values(data.questionRegistry)
      .filter((entry) => {
        if (paper && entry.paper !== paper) return false;
        return !entry.lastAttemptCorrect || entry.timesCorrect < entry.timesSeen;
      })
      .map((entry) => entry.questionId);
  }

  /** Check if a specific question was previously missed or has mistakes */
  static isQuestionIncorrect(questionId: string): boolean {
    const data = this.load();
    const entry = data.questionRegistry[questionId];
    if (!entry) return false;
    return !entry.lastAttemptCorrect || entry.timesCorrect < entry.timesSeen;
  }

  /** Get count of completed 5-question sets for a given paper domain */
  static getCompletedSetCount(paper: QuestPaperDomain): number {
    const data = this.load();
    return data.completedSetCounts[paper] || 0;
  }

  /** Update ongoing correct streak */
  static incrementStreak(): number {
    const data = this.load();
    data.currentStreak += 1;
    if (data.currentStreak > data.bestStreakOverall) {
      data.bestStreakOverall = data.currentStreak;
    }
    this.save(data);
    return data.currentStreak;
  }

  /** Reset current streak without penalty to overall best */
  static resetStreak(): void {
    const data = this.load();
    data.currentStreak = 0;
    this.save(data);
  }

  /** Reset question attempt history for a specific paper or all papers */
  static resetQuestionHistory(paper?: QuestPaperDomain): void {
    const data = this.load();
    if (!paper) {
      data.questionRegistry = {};
      data.completedSetCounts = { paper1: 0, paper2_maths: 0, paper2_nvr: 0 };
    } else {
      for (const [id, entry] of Object.entries(data.questionRegistry)) {
        if (entry.paper === paper) {
          delete data.questionRegistry[id];
        }
      }
      data.completedSetCounts[paper] = 0;
    }
    this.save(data);
  }

  /** Clear all sprint data (for testing) */
  static clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignored
    }
  }
}
